<template>
  <div class="mb-0pt">
    <div class="page-section">
      <div :class="pageContainer">
        <!-- Tabs for separate referred programs -->
        <div v-if="get(getCurrentSchool, 'referred_program_seperate_tab', false)" class="mb-3">
          <b-tabs v-model="activeTab" content-class="mt-3">
            <b-tab title="All Programs" active>
              <template #title>
                <span>School Programs</span>
              </template>
            </b-tab>
            <b-tab title="Referred Programs">
              <template #title>
                <span>Referred Programs</span>
              </template>
            </b-tab>
          </b-tabs>
        </div>

        <!-- Referred Programs Disclaimer -->
        <div v-if="get(getCurrentSchool, 'referred_program_seperate_tab', false) && activeTab === 1" class="alert alert-info mb-3" role="alert">
          <i class="fas fa-info-circle mr-2"></i>
          <strong>Referral Program Notice:</strong> All programs shown in this section are Referral Programs offered by third-party training providers and are not programs of or operated by {{ get(getCurrentSchool, 'name', 'this school') }}. By selecting a Referral Program, you understand you will be redirected to a third-party website. {{ get(getCurrentSchool, 'name', 'This school') }} does not control and is not responsible for the program content, availability, pricing, admissions decisions, or student outcomes of third-party providers.
        </div>

        <div class="d-flex flex-wrap justify-content-end">
          <div :class="isTabScreen ? 'col-lg-7 px-0' : ' col-sm-5 col-md-3 mb-2 px-1'">
            <v-select
              id="program-type"
              v-model="programType"
              class="form-control v-select-custom"
              placeholder="Select program type"
              label="text"
              :reduce="type => type.value"
              :options="typeOptions"
              style="height: auto"
              @input="onProgramTypeChange"
            >
              <template #search="{ attributes, events }">
                <input class="vs__search" v-bind="attributes" v-on="events" />
              </template>
              <template slot="option" slot-scope="option">
                <span>{{ option.text }}</span>
              </template>
              <template slot="selected-option" slot-scope="option">
                {{ truncate(option.text, { length: 20 }) }}
              </template>
            </v-select>
          </div>
        </div>

        <page-separator
          :title="
            isLoading ? '' : `Showing ${displayTotalPrograms ? `${$n(displayProgramsCount)}` : 0} of ${$n(displayTotalPrograms)} Programs`
          "
        />

        <div class="page-section d-flex justify-content-center" v-if="!displayPrograms.length && !isLoading">
          <div class="card card-block card-stretch card-height col-md-8">
            <div class="card-body text-center">
              <i class="fas fa-exclamation-circle fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
              <h4>No Programs Found</h4>

              <p class="text-muted">Couldn't find any programs.</p>
            </div>
          </div>
        </div>

        <b-skeleton-wrapper :loading="isLoading" v-else>
          <template #loading>
            <div class="row">
              <div class="col-lg-4 col-md-4 col-sm-6" v-for="item in [1, 2, 3]" :key="item">
                <div class="card card-sm card--elevated p-relative">
                  <span class="js-image">
                    <b-skeleton-img width="100%" class="pb-0 mb-0"></b-skeleton-img>
                  </span>

                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex">
                        <a class="card-title" href="#" @click.prevent><b-skeleton width="70%"></b-skeleton></a>
                        <small class="text-50 font-weight-bold mb-4pt"><b-skeleton width="50%"></b-skeleton></small>
                      </div>
                      <a href="#" class="ml-4pt material-icons text-20 card-course__icon-favorite" @click.prevent
                        >more_vert</a
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div class="row card-group-row mb-lg-12pt home-card p-0" style="">
            <div v-for="program in displayPrograms" :key="program.id" class="col-lg-4 col-md-6 col-sm-6 m-0">
              <program-card :program="program" :next-class="nextClassObj" />
            </div>
          </div>
          <div class="d-flex justify-content-center" v-if="displayProgramsCount < displayTotalPrograms">
            <a>
              <router-link :to="{ name: 'public-sch-programs' }" class="btn btn-primary mb-3">View more</router-link></a
            >
          </div>
        </b-skeleton-wrapper>
      </div>
    </div>
  </div>
</template>

<script>
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { mapGetters, mapActions } from 'vuex';
import vSelect from 'vue-select';
import Page from '@/components/Page.vue';
import { cloneDeep, debounce, get, keyBy, truncate } from 'lodash';
import ProgramCard from './ProgramCard.vue';
import { USER_ROLES, PROGRAM_STATUSES, USER_ROLE_TYPES } from '../../../common/constants';

export default {
  components: {
    PageSeparator,
    ProgramCard,
    vSelect,
  },
  head() {
    return {
      meta: [
        {
          name: 'keywords',
          content: (get(this.getCurrentSchool, 'meta_tags', []) || []).toString(),
        },
        {
          name: 'description',
          content: get(this.getCurrentSchool, 'pp_home_description'),
        },
      ],
    };
  },
  extends: Page,
  props: {
    pageContainer: { type: String, default: 'container page__container' },
  },
  data() {
    return {
      title: 'Home',

      isLoading: true,
      schoolId: null,
      perPage: 18,
      currentPage: 1,
      totalPrograms: 0,
      ordering: 'title',
      programType: '',
      programs: [],
      refPrograms: [],
      typeOptions: [
        { value: 'virtual', text: 'Online' },
        { value: 'onsite', text: 'In-Person' },
        { value: 'blended', text: 'Hybrid' },
      ],
      USER_ROLES,
      PROGRAM_STATUSES,
      USER_ROLE_TYPES,
      programsCount: 0,
      searchTerm: '',
      pageFromCount: 0,
      pageToCount: 0,
      nextClasses: [],
      nextClassObj: {},
      areClassesLoading: false,
      areRefLoading: false,
      activeTab: 0,
      // Separate filters for All Programs tab
      allProgramsFilters: {
        programType: '',
      },
      // Separate filters for Referred Programs tab
      referredProgramsFilters: {
        programType: '',
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    ...mapGetters('school', ['getCurrentSchool']),
    ...mapGetters('program', ['getProgramType']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: this.$t('Programs'), active: true },
      ];
    },
    isMobSmallScreen() {
      return this.windowWidth <= 420;
    },
    isTabScreen() {
      return this.windowWidth <= 776;
    },
    displayPrograms() {
      const hasSeparateTab = get(this.getCurrentSchool, 'referred_program_seperate_tab', false);
      if (hasSeparateTab) {
        return this.activeTab === 0 ? this.programs : this.refPrograms;
      }
      return this.programs;
    },
    displayProgramsCount() {
      const hasSeparateTab = get(this.getCurrentSchool, 'referred_program_seperate_tab', false);
      if (hasSeparateTab) {
        return this.activeTab === 0 ? this.programsCount : this.refPrograms.length;
      }
      return this.programsCount;
    },
    displayTotalPrograms() {
      const hasSeparateTab = get(this.getCurrentSchool, 'referred_program_seperate_tab', false);
      if (hasSeparateTab) {
        return this.activeTab === 0 ? this.totalPrograms : this.refPrograms.length;
      }
      return this.totalPrograms;
    },
  },
  watch: {
    getCurrentSchool: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.schoolId = value;
          this.fetchPrograms();

          this.completeHeadTitle = `${get(this.getCurrentSchool, 'name', 'Home')} - ${get(
            this.getCurrentSchool,
            'pp_home_title',
            'Transition Enroll'
          )}`;
        }
      },
    },
    activeTab(newTab, oldTab) {
      const hasSeparateTab = get(this.getCurrentSchool, 'referred_program_seperate_tab', false);
      if (!hasSeparateTab) return;

      // Save current tab filters before switching
      if (oldTab === 0) {
        this.allProgramsFilters = {
          programType: this.programType,
        };
      } else {
        this.referredProgramsFilters = {
          programType: this.programType,
        };
      }

      // Load filters for new tab
      if (newTab === 0) {
        this.programType = this.allProgramsFilters.programType;
        this.fetchPrograms();
      } else {
        this.programType = this.referredProgramsFilters.programType;
        this.fetchReferredPrograms();
      }
    },
  },

  methods: {
    ...mapActions('program', [
      'getSchoolOfferedPrograms',
      'getProgramNextClass',
      'getSchoolReferredPrograms',
      'updateProgramType',
    ]),
    get,
    truncate,
    async onProgramTypeChange(value) {
      this.updateProgramType(value);
      const hasSeparateTab = get(this.getCurrentSchool, 'referred_program_seperate_tab', false);
      
      if (hasSeparateTab && this.activeTab === 1) {
        // If on referral tab, fetch referred programs
        this.fetchReferredPrograms();
      } else {
        // Otherwise fetch regular programs
        this.fetchPrograms();
      }
    },
    async fetchNextClass() {
      this.areClassesLoading = true;
      const response = await this.getProgramNextClass({ programs: this.programs.map(cs => cs.id) });
      this.nextClasses = response.data;
      this.nextClassObj = keyBy(response.data, 'program');
      this.areClassesLoading = false;
    },
    async fetchPrograms(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      const hasSeparateTab = get(this.getCurrentSchool, 'referred_program_seperate_tab', false);
      if (pageNum == 1 && !hasSeparateTab) {
        await this.fetchReferredPrograms();
      }
      const response = await this.getSchoolOfferedPrograms({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        ...(this.programType && { type: this.programType }),
        ...(this.searchTerm && { search: this.searchTerm }),
        school: get(this.getCurrentSchool, 'id'),
        ...params,
      });
      this.programs = response.data.results;
      this.currentPage = pageNum;
      this.totalPrograms = response.data.count;
      this.programsCount = get(response.data, 'results.length', 0);
      this.fetchNextClass();
      this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
      this.pageToCount = this.pageFromCount + this.programsCount - 1;
      
      if (!hasSeparateTab && this.refPrograms.length) {
        // Adding referred programs after every 5th program (existing behavior)
        const programs = cloneDeep(this.programs);
        let count = 1;
        const refProgramsCopy = cloneDeep(this.refPrograms);
        this.programs.forEach((program, index) => {
          if ((index + 1) % 5 === 0 && refProgramsCopy.length) {
            programs.splice(index + count, 0, { ...refProgramsCopy.shift(), prgType: 'referred' });
            count++;
          }
        });
        this.programs = programs;
        if (refProgramsCopy.length && this.pageToCount >= this.totalPrograms) {
          refProgramsCopy.forEach(program => {
            this.programs.push({ ...program, prgType: 'referred' });
          });
        }
      }

      this.isLoading = false;
    },

    async fetchReferredPrograms(params = {}) {
      this.areRefLoading = true;

      const response = await this.getSchoolReferredPrograms({
        school: get(this.getCurrentSchool, 'id'),
        ...(this.programType && { type: this.programType }),
        ...params,
      });
      this.refPrograms = response.data.map(program => ({ ...program, prgType: 'referred' }));

      this.areRefLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchPrograms(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchPrograms();
    },

    onSearch() {
      this.debouncedSearchPrograms(this);
    },

    debouncedSearchPrograms: debounce(vm => {
      vm.fetchPrograms();
    }, 500),
  },

  async mounted() {
    this.programType = this.getProgramType;
  },
};
</script>
